import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    useCallback,
    useEffect,
    useRef,
    type FocusEvent,
    type FocusEventHandler,
} from "react";

/**
 * Whether `target` is part of the picker whose chain is `chainId`: its
 * trigger (`data-picker-trigger`) or any of its popups, flyout levels
 * included (`data-picker-chain`). Wherever the popups are portalled.
 */
const isInsidePicker = ({
    target,
    chainId,
}: {
    target: EventTarget | null;
    chainId: string;
}) =>
    target instanceof Element &&
    target.closest(
        `[data-picker-trigger="${chainId}"], [data-picker-chain="${chainId}"]`,
    ) !== null;

/**
 * The form-field `onBlur` of a list root: called once focus leaves the
 * picker as a whole, never while it moves between the trigger and the
 * popups. The consumer gets the focus event of the last element that lost
 * focus; `onBeforeTriggerBlur` still runs on every trigger blur.
 */
export const usePickerFocusLeave = ({
    chainId,
    onBeforeTriggerBlur,
    onBlur,
}: {
    chainId: string;
    onBeforeTriggerBlur?: (event: FocusEvent<HTMLElement>) => void;
    onBlur?: FocusEventHandler<HTMLElement>;
}) => {
    const handlersRef = useLatestRef({ onBeforeTriggerBlur, onBlur });
    const pendingLeaveRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    useEffect(() => () => clearTimeout(pendingLeaveRef.current), []);

    const cancelPendingLeave = useCallback(() => {
        clearTimeout(pendingLeaveRef.current);
    }, []);

    // Focus moving to another element may be a hop: Base UI routes Tab
    // through focus guards and restores focus after a blur, and a control
    // inside a popup can portal an overlay (whose focus reaches the popup's
    // `onFocus`, which cancels). So the leave is judged where focus settles.
    const reportLeaveOnceSettled = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            cancelPendingLeave();
            if (
                handlersRef.current.onBlur === undefined ||
                isInsidePicker({ target: event.relatedTarget, chainId })
            ) {
                return;
            }
            const doc = event.currentTarget.ownerDocument;
            pendingLeaveRef.current = setTimeout(() => {
                if (!isInsidePicker({ target: doc.activeElement, chainId })) {
                    handlersRef.current.onBlur?.(event);
                }
            }, 0);
        },
        [cancelPendingLeave, handlersRef, chainId],
    );

    const handleTriggerBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            handlersRef.current.onBeforeTriggerBlur?.(event);
            if (event.relatedTarget === null) {
                cancelPendingLeave();
                handlersRef.current.onBlur?.(event);
                return;
            }
            reportLeaveOnceSettled(event);
        },
        [cancelPendingLeave, handlersRef, reportLeaveOnceSettled],
    );

    return {
        handleTriggerBlur,
        handlePopupBlur: reportLeaveOnceSettled,
        handlePopupFocus: cancelPendingLeave,
    };
};
